import { useEffect, type ReactNode } from 'react';

interface ModalProps {
  title: string;
  onClose: () => void;
  children: ReactNode;
  /** Wider panel for content-heavy modals (e.g. an import summary table). Default ~480px. */
  wide?: boolean;
  /** Explicit max width in px for a modal holding a wide grid (overrides `wide`). */
  maxWidth?: number;
}

/**
 * Minimal centered-panel popup: backdrop (click to close), Escape to
 * close, no focus trap (acceptable for this app's current modal count -
 * add one if/when a modal holds a long form). First real use: the
 * Products/Services bulk-import popup.
 */
export function Modal({ title, onClose, children, wide, maxWidth }: ModalProps) {
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <div
      className="ne-modal__backdrop"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="ne-modal__panel"
        style={maxWidth ? { maxWidth } : wide ? { maxWidth: 720 } : undefined}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="ne-modal__header">
          <h2 className="ne-modal__title">{title}</h2>
          <button
            type="button"
            className="ne-modal__close"
            onClick={onClose}
            aria-label="Close dialog"
          >
            &times;
          </button>
        </div>
        <div className="ne-modal__body">{children}</div>
      </div>
    </div>
  );
}
